import React from 'react';
import capitalize from 'lodash/capitalize';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import Badge from '@atlaskit/badge';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives';
import { N0 } from '@atlaskit/theme/colors';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { getUserTier } from '@atlassian/cssxp-utils-entitlement';
import { CustomPanel, EntitlementIcon, SummaryPanelContainer, SummaryPanelRow } from '@css-xp/base';
import { isTrialEntitlement } from '@css-xp/entitlements-v2';
import { BillingFrequency, EntitlementDetails, PricingPlan } from '@css-xp/graphtypes/src/generated-types-new';
import { NoData } from '@css-xp/ppc-common';
import { isCollectionProductEntitlement, pluralize } from '@css-xp/ppc-utils';
import { getPricingPlanDescription } from './utils/change-subscription-pane-utils';
import './plan-summary.scss';

interface Props {
  entitlement: EntitlementDetails;
  isBillingCycleChanged?: boolean;
  isExistingAnnualPlanForJCA?: boolean;
  heading?: string;
  isProductIconRequired?: boolean;
  isDesignIteration1Enabled?: boolean;
  isTWCPhase2Enabled?: boolean;
  linkedSubscriptions?: Array<{ id: string; entitlementNumber: string; productId: string; productName: string }>;
}

export const CurrentPlanSummary = ({
  entitlement,
  isBillingCycleChanged,
  isExistingAnnualPlanForJCA,
  heading,
  isProductIconRequired,
  isDesignIteration1Enabled,
  isTWCPhase2Enabled,
  linkedSubscriptions,
}: Props) => {
  const billingCycle = entitlement?.pricingPlan?.billingFrequency ?? BillingFrequency.MONTHLY;

  const isCollectionProduct = isTWCPhase2Enabled && isCollectionProductEntitlement(entitlement?.productId);

  const isMonthlySubscription = billingCycle === BillingFrequency.MONTHLY;

  // To improve: isTrial logic is same as what is used here:
  // partner-purchase-center/pages/entitlement/src/ui/components/entitlement-details/entitlement-details.tsx.
  // Need to check and implement this logic to a common place.
  const isFreePlan = entitlement.edition ? entitlement.edition.includes('Free') : false;
  const isStatusPage = entitlement?.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isTrial = !isFreePlan && isTrialEntitlement(entitlement, isStatusPage);

  const renderProductName = () => {
    const { productName, productId } = entitlement;
    //Show product icon only if product icon is required and product id is available
    if (isProductIconRequired && entitlement?.productId && productName) {
      return (
        <Flex gap="space.100">
          <EntitlementIcon productId={productId} className="avatar medium" isCloud />
          <p className="text-ellipsis">{productName}</p>
        </Flex>
      );
    }

    return <p>{defaultTo(productName, 'Unnamed product')}</p>;
  };

  const renderCurrentPlanDetails = () => {
    if (isDesignIteration1Enabled) {
      return null;
    }
    return <SummaryPanelRow label="Current plan" value={<p>{entitlement?.offering?.name}</p>} />;
  };

  const linkedSubscriptionPanelHeader = () => {
    return (
      <Box paddingInlineStart="space.400">
        <Flex gap="space.100" alignItems="center">
          {/* Above 500 it will show 500+ in badge */}
          <Badge max={500}>{linkedSubscriptions?.length}</Badge>
          <Text size="small" weight="bold">
            {isCollectionProduct ? 'included products' : 'linked subscriptions'}
          </Text>
        </Flex>
      </Box>
    );
  };

  return (
    <div className="plan-summary">
      <SummaryPanelContainer isHeaderContentHidden={true}>
        {heading && <h4>{heading}</h4>}
        <SummaryPanelRow label="Product name" value={renderProductName()} />
        <SummaryPanelRow label="Entitlement number" value={<p>{entitlement?.entitlementNumber}</p>} />
        <SummaryPanelRow label="Billing cycle" value={<p>{capitalize(billingCycle)}</p>} />
        {renderCurrentPlanDetails()}
        <SummaryPanelRow label="Trial" value={<p>{isTrial ? 'Yes' : 'No'}</p>} />
        <SummaryPanelRow
          label="Current pricing plan"
          value={<p>{getPricingPlanDescription(entitlement?.pricingPlan as PricingPlan)}</p>}
        />
        {(isBillingCycleChanged || isExistingAnnualPlanForJCA || isMonthlySubscription) && (
          <SummaryPanelRow
            label={isDesignIteration1Enabled ? 'Quantity' : 'Active users'}
            value={<p>{isNil(entitlement?.activeUsers) ? <NoData /> : pluralize(entitlement?.activeUsers, 'user')}</p>}
          />
        )}
        {isExistingAnnualPlanForJCA && (
          <SummaryPanelRow
            label={'Current user tier'}
            value={
              <p>{!isNil(getUserTier(entitlement?.subscription)) ? getUserTier(entitlement?.subscription) : '-'}</p>
            }
          />
        )}
        {(linkedSubscriptions || []).length > 0 && (
          <CustomPanel
            testId="linked-subscription-change-subscription-pane"
            expandedHeader={linkedSubscriptionPanelHeader()}
            collapsedHeader={linkedSubscriptionPanelHeader()}
            version={3}
            backgroundColor={N0}
          >
            <Box paddingInline="space.250" paddingBlockStart="space.075" paddingBlockEnd="space.150">
              <Stack space="space.100">
                {(linkedSubscriptions || []).map((linkedSubscription) => {
                  return (
                    <Flex key={linkedSubscription?.id}>
                      <Flex alignItems="center">
                        <Box paddingInline="space.100">
                          <EntitlementIcon
                            productId={linkedSubscription?.productId}
                            className="avatar medium"
                            isCloud
                          />
                        </Box>
                      </Flex>
                      <Flex direction="column">
                        <Text color="color.text" size="small">
                          {linkedSubscription?.productName}
                        </Text>
                        <Text color="color.text.subtlest" size="small">
                          {linkedSubscription?.entitlementNumber}
                        </Text>
                      </Flex>
                    </Flex>
                  );
                })}
              </Stack>
            </Box>
          </CustomPanel>
        )}
      </SummaryPanelContainer>
    </div>
  );
};
